'use client';

import { useEffect, useRef } from 'react';

const pillars = [
  {
    title: 'Commitment to Quality',
    body: 'Every blind we manufacture passes through multiple quality checks before leaving our workshop. We use premium fabrics, precision-cut components, and hardware built to last — because a window treatment is a long-term investment in your space.',
    img: 'https://res.cloudinary.com/dsizaxsjm/image/upload/v1773518496/blinds_factory_s46skt.jpg',
    alt: 'Wooden venetian blinds with warm light filtering through slats',
    accent: '#8B6542',
  },
  {
    title: 'Customer Satisfaction',
    body: 'From your first enquiry to the final installation, we\'re with you every step. Our team listens first, recommends second — ensuring every solution fits your space, lifestyle, and budget, not just a catalogue.',
    img: 'https://res.cloudinary.com/dsizaxsjm/image/upload/v1773518678/customer_qcdbih.jpg',
    alt: 'Bright modern living room with roller blinds filtering natural sunlight',
    accent: '#6B7B5C',
  },
  {
    title: 'Innovation & Design',
    body: 'We continuously explore new fabrics, mechanisms, and smart solutions — from motorised systems to monsoon-proof PVC — so you always have access to what\'s current, functional, and beautiful.',
    img: 'https://res.cloudinary.com/dsizaxsjm/image/upload/v1773519260/innovation2_ujosah.jpg',
    alt: 'Elegant sheer curtains in a contemporary room with soft diffused light',
    accent: '#5C6B7B',
  },
  {
    title: 'Sustainability',
    body: 'We source responsibly, minimise waste in our manufacturing process, and offer eco-conscious fabric options. Good business, in our view, means being accountable to the environment as well as to our customers.',
    img: 'https://res.cloudinary.com/dsizaxsjm/image/upload/v1773519355/sustainability2_km6sg4.jpg',
    alt: 'Natural bamboo and woven blinds in a warm sunlit interior',
    accent: '#5C7B6B',
  },
  {
    title: 'Integrity & Transparency',
    body: 'We quote honestly, deliver on time, and stand behind our work. If something isn\'t right, we fix it — no runaround, no fine print. That\'s the promise Naseer Ahmed made in 1991, and it holds today.',
    img: 'https://res.cloudinary.com/dsizaxsjm/image/upload/v1773519531/integrity2_cqxwmq.jpg',
    alt: 'Serene interior with soft fabric blinds and clean architecture',
    accent: '#7B5C6B',
  },
];

export default function CommitmentSection() {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('pf-card-visible')),
      { threshold: 0.12 }
    );
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style data-component-id="src/app/about/components/CommitmentSection.tsx:57:6" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="57" data-component-end-line="241" data-component-file="CommitmentSection.tsx" data-component-name="style" data-component-content="%7B%22elementName%22%3A%22style%22%7D">{`
        .pf-commit-section {
          background: var(--color-background);
          padding: 96px 24px 112px;
          font-family: 'Inter', sans-serif;
        }

        /* Section Header */
        .pf-commit-header {
          max-width: 960px;
          margin: 0 auto 72px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          align-items: end;
        }
        @media (max-width: 680px) {
          .pf-commit-header { grid-template-columns: 1fr; gap: 16px; }
        }
        .pf-commit-eyebrow {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--color-accent);
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 14px;
        }
        .pf-commit-eyebrow::before {
          content: '';
          display: block;
          width: 32px;
          height: 1px;
          background: var(--color-accent);
          flex-shrink: 0;
        }
        .pf-commit-title {
          font-family: 'Inter', sans-serif;
          font-size: clamp(32px, 5vw, 52px);
          font-weight: 600;
          line-height: 1.1;
          color: var(--color-foreground);
          margin: 0;
        }
        .pf-commit-title em {
          font-style: italic;
          color: var(--color-accent);
        }
        .pf-commit-desc {
          font-size: 15px;
          line-height: 1.8;
          color: var(--color-muted-foreground);
          margin: 0;
          align-self: end;
        }

        /* Card Grid — first 3 in a row, last 2 centred */
        .pf-commit-grid {
          max-width: 960px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        @media (max-width: 840px) {
          .pf-commit-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 540px) {
          .pf-commit-grid { grid-template-columns: 1fr; }
        }

        /* Card */
        .pf-commit-card {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.6s ease, transform 0.6s ease;
          background: var(--color-card);
          border: 1px solid var(--color-border);
          border-radius: 16px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          cursor: default;
        }
        .pf-commit-card.pf-card-visible { opacity: 1; transform: none; }
        .pf-commit-card:nth-child(1) { transition-delay: 0ms; }
        .pf-commit-card:nth-child(2) { transition-delay: 90ms; }
        .pf-commit-card:nth-child(3) { transition-delay: 180ms; }
        .pf-commit-card:nth-child(4) { transition-delay: 270ms; }
        .pf-commit-card:nth-child(5) { transition-delay: 360ms; }

        /* 4th and 5th card nudge to centre on 3-col grid */
        @media (min-width: 841px) {
          .pf-commit-card:nth-child(4) { grid-column: 1; margin-left: calc(50% + 12px); }
          .pf-commit-card:nth-child(5) { grid-column: 2; }
          /* Reset — handled via JS-free CSS trick below */
          .pf-commit-card:nth-child(4),
          .pf-commit-card:nth-child(5) {
            margin-left: 0;
          }
          .pf-commit-row-2 {
            display: flex;
            justify-content: center;
            gap: 24px;
            max-width: 960px;
            margin: 24px auto 0;
            }

            .pf-commit-row-2 .pf-commit-card {
            flex: 0 0 calc(33.333% - 16px);
            max-width: calc(33.333% - 16px);
            }
          .pf-commit-row-2 .pf-commit-card:first-child {
            grid-column: 1;
            margin-left: calc(50% + 12px);
          }
        }
        @media (max-width: 840px) {
          .pf-commit-row-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; max-width: 960px; margin: 0 auto; }
        }
        @media (max-width: 540px) {
          .pf-commit-row-2 { grid-template-columns: 1fr; }
        }

        /* Image */
        .pf-card-img {
          width: 100%;
          height: 200px;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }
        .pf-commit-card:hover .pf-card-img {
          transform: scale(1.04);
        }
        .pf-card-img-wrap {
          overflow: hidden;
          flex-shrink: 0;
        }

        /* Body */
        .pf-card-body { padding: 24px 24px 28px; flex: 1; display: flex; flex-direction: column; gap: 10px; }
        .pf-card-accent-line {
          width: 28px;
          height: 2px;
          border-radius: 2px;
          margin-bottom: 4px;
          flex-shrink: 0;
        }
        .pf-card-title {
          font-family: 'Inter', sans-serif;
          font-size: 20px;
          font-weight: 600;
          color: var(--color-foreground);
          margin: 0;
          line-height: 1.2;
        }
        .pf-card-body p {
          font-size: 13.5px;
          line-height: 1.8;
          color: var(--color-muted-foreground);
          margin: 0;
          flex: 1;
        }

        /* Bottom rule */
        .pf-commit-rule {
          max-width: 960px;
          margin: 64px auto 0;
          display: flex;
          align-items: center;
          gap: 24px;
        }
        .pf-commit-rule::before,
        .pf-commit-rule::after { content: ''; flex: 1; height: 1px; background: var(--color-border); }
        .pf-commit-rule span {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-style: italic;
          color: var(--color-muted-foreground);
          white-space: nowrap;
        }
      `}</style>

      <section data-component-id="src/app/about/components/CommitmentSection.tsx:243:6" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="243" data-component-end-line="302" data-component-file="CommitmentSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22pf-commit-section%22%7D" className="pf-commit-section">
        {/* Header */}
        <div data-component-id="src/app/about/components/CommitmentSection.tsx:245:8" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="245" data-component-end-line="256" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-commit-header%22%7D" className="pf-commit-header">
          <div data-component-id="src/app/about/components/CommitmentSection.tsx:246:10" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="246" data-component-end-line="251" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <p data-component-id="src/app/about/components/CommitmentSection.tsx:247:12" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="247" data-component-end-line="247" data-component-file="CommitmentSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22pf-commit-eyebrow%22%2C%22textContent%22%3A%22Why%20Choose%20Us%22%7D" className="pf-commit-eyebrow">Why Choose Us</p>
            <h2 data-component-id="src/app/about/components/CommitmentSection.tsx:248:12" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="248" data-component-end-line="250" data-component-file="CommitmentSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22pf-commit-title%22%2C%22textContent%22%3A%22Commitment%20to%22%7D" className="pf-commit-title">
              Commitment to<br data-component-id="src/app/about/components/CommitmentSection.tsx:249:27" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="249" data-component-end-line="249" data-component-file="CommitmentSection.tsx" data-component-name="br" data-component-content="%7B%22elementName%22%3A%22br%22%7D" /><em data-component-id="src/app/about/components/CommitmentSection.tsx:249:33" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="249" data-component-end-line="249" data-component-file="CommitmentSection.tsx" data-component-name="em" data-component-content="%7B%22elementName%22%3A%22em%22%2C%22textContent%22%3A%22Quality%20Craftsmanship%22%7D">Quality Craftsmanship</em>
            </h2>
          </div>
          <p data-component-id="src/app/about/components/CommitmentSection.tsx:252:10" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="252" data-component-end-line="255" data-component-file="CommitmentSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22pf-commit-desc%22%2C%22textContent%22%3A%22Three%20decades%20of%20manufacturing%20custom%20blinds%20across%20Bengaluru%20has%20taught%20us%20one%20thing%20%E2%80%94%5Cn%20%20%20%20%20%20%20%20%20%20%20%20there%20are%20no%20shortcuts%20worth%20taking.%20Here's%20what%20we%20stand%20for.%22%7D" className="pf-commit-desc">
            Three decades of manufacturing custom blinds across Bengaluru has taught us one thing —
            there are no shortcuts worth taking. Here's what we stand for.
          </p>
        </div>

        {/* Row 1 — 3 cards */}
        <div data-component-id="src/app/about/components/CommitmentSection.tsx:259:8" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="259" data-component-end-line="276" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-commit-grid%22%7D" className="pf-commit-grid">
          {pillars.slice(0, 3).map((p, i) => (
            <div data-component-id="src/app/about/components/CommitmentSection.tsx:261:12" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="261" data-component-end-line="274" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-commit-card%22%7D"
              key={p.title}
              className="pf-commit-card"
              ref={(el) => { cardRefs.current[i] = el; }}
            >
              <div data-component-id="src/app/about/components/CommitmentSection.tsx:266:14" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="266" data-component-end-line="268" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-card-img-wrap%22%7D" className="pf-card-img-wrap">
                <img data-component-id="src/app/about/components/CommitmentSection.tsx:267:16" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="267" data-component-end-line="267" data-component-file="CommitmentSection.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22pf-card-img%22%7D" src={p.img} alt={p.alt} className="pf-card-img" loading="lazy" />
              </div>
              <div data-component-id="src/app/about/components/CommitmentSection.tsx:269:14" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="269" data-component-end-line="273" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-card-body%22%7D" className="pf-card-body">
                <div data-component-id="src/app/about/components/CommitmentSection.tsx:270:16" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="270" data-component-end-line="270" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-card-accent-line%22%7D" className="pf-card-accent-line" style={{ background: p.accent }} />
                <h3 data-component-id="src/app/about/components/CommitmentSection.tsx:271:16" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="271" data-component-end-line="271" data-component-file="CommitmentSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22pf-card-title%22%7D" className="pf-card-title">{p.title}</h3>
                <p data-component-id="src/app/about/components/CommitmentSection.tsx:272:16" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="272" data-component-end-line="272" data-component-file="CommitmentSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D">{p.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Row 2 — 2 cards, centred */}
        <div data-component-id="src/app/about/components/CommitmentSection.tsx:279:8" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="279" data-component-end-line="296" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-commit-row-2%22%7D" className="pf-commit-row-2">
          {pillars.slice(3).map((p, i) => (
            <div data-component-id="src/app/about/components/CommitmentSection.tsx:281:12" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="281" data-component-end-line="294" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-commit-card%22%7D"
              key={p.title}
              className="pf-commit-card"
              ref={(el) => { cardRefs.current[i + 3] = el; }}
            >
              <div data-component-id="src/app/about/components/CommitmentSection.tsx:286:14" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="286" data-component-end-line="288" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-card-img-wrap%22%7D" className="pf-card-img-wrap">
                <img data-component-id="src/app/about/components/CommitmentSection.tsx:287:16" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="287" data-component-end-line="287" data-component-file="CommitmentSection.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22pf-card-img%22%7D" src={p.img} alt={p.alt} className="pf-card-img" loading="lazy" />
              </div>
              <div data-component-id="src/app/about/components/CommitmentSection.tsx:289:14" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="289" data-component-end-line="293" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-card-body%22%7D" className="pf-card-body">
                <div data-component-id="src/app/about/components/CommitmentSection.tsx:290:16" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="290" data-component-end-line="290" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-card-accent-line%22%7D" className="pf-card-accent-line" style={{ background: p.accent }} />
                <h3 data-component-id="src/app/about/components/CommitmentSection.tsx:291:16" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="291" data-component-end-line="291" data-component-file="CommitmentSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22pf-card-title%22%7D" className="pf-card-title">{p.title}</h3>
                <p data-component-id="src/app/about/components/CommitmentSection.tsx:292:16" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="292" data-component-end-line="292" data-component-file="CommitmentSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D">{p.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Divider quote */}
        <div data-component-id="src/app/about/components/CommitmentSection.tsx:299:8" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="299" data-component-end-line="301" data-component-file="CommitmentSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pf-commit-rule%22%7D" className="pf-commit-rule">
          <span data-component-id="src/app/about/components/CommitmentSection.tsx:300:10" data-component-path="src/app/about/components/CommitmentSection.tsx" data-component-line="300" data-component-end-line="300" data-component-file="CommitmentSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Est.%201991%20%C2%B7%20Bengaluru%20%C2%B7%20Nazeer%20Ahmed%20%26%20Naoman%20Ahmed%22%7D">Est. 1991 · Bengaluru · Nazeer Ahmed &amp; Naoman Ahmed</span>
        </div>
      </section>
    </>
  );
}
